import {
  ChannelUnsubscribe,
  channelClient,
  ContractShapeOf,
  RequestPolicies,
  ContractEvent,
  ContractEventOf,
} from 'shared/channels';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type {
  DocumentationCreateModel,
  DocumentationEditModel,
  DocumentationModel,
  DocumentationVersionEditModel,
} from './documentationModel';
import {
  documentationContract,
  DocumentationRestoreRequest,
  DocumentationSignedUrls,
  FilteredDirectoryRequest,
  FilteredDirectoryResponse,
} from './documentationContract';
import type { GetDocumentationSignedRequestModel } from './getDocumentationSignedRequestModel';
import type {
  DocumentationOperationPreflightRequest,
  DocumentationOperationPreflightResponse,
} from './operationPreflight';

export interface SearchDocumentationsOptions {
  skipResync?: boolean;
}

/** Documentation as served by the service worker. */
export const documentationClient = {
  getOneById(id: string): Promise<DocumentationModel> {
    return channelClient.request(documentationContract, 'getOneById', { id }, RequestPolicies.read);
  },

  getChildren(parentId: string): Promise<DocumentationModel[]> {
    return channelClient.request(documentationContract, 'getChildren', { parentId }, RequestPolicies.read);
  },

  getFilteredDirectory(request: FilteredDirectoryRequest): Promise<FilteredDirectoryResponse> {
    return channelClient.request(
      documentationContract,
      'getFilteredDirectory',
      request,
      RequestPolicies.bulkRead
    );
  },

  search(searchPhrase: string, options?: SearchDocumentationsOptions): Promise<DocumentationModel[]> {
    return channelClient.request(
      documentationContract,
      'search',
      { searchPhrase, skipResync: options?.skipResync },
      RequestPolicies.bulkRead
    );
  },

  preflight(request: DocumentationOperationPreflightRequest): Promise<DocumentationOperationPreflightResponse> {
    return channelClient.request(documentationContract, 'preflight', request, RequestPolicies.read);
  },

  /**
   * Resolves with the local id once the documentation is saved locally (the
   * upload follows). `uniqueId` comes back with the `createdEntity` event.
   * Long-running: an upload of hundreds of files queues as many saves in the
   * worker, and the last ones would pass the mutation timeout (the legacy
   * save had none).
   */
  create(documentation: DocumentationCreateModel, uniqueId?: string): Promise<number> {
    return channelClient.request(
      documentationContract,
      'create',
      { documentation, uniqueId },
      RequestPolicies.longRunning
    );
  },

  /** Resolves once the edit is saved locally (the upload follows). */
  edit(documentation: DocumentationEditModel): Promise<void> {
    return channelClient.request(documentationContract, 'edit', documentation, RequestPolicies.mutation);
  },

  editVersion(documentationVersion: DocumentationVersionEditModel): void {
    channelClient.notify(documentationContract, 'editVersion', documentationVersion);
  },

  remove(documentations: number[], directories: number[], signature?: string): void {
    channelClient.notify(documentationContract, 'remove', { documentations, directories, signature });
  },

  restore(documentations: number[], directories: number[] = [], signature?: string): void {
    const request: DocumentationRestoreRequest = { documentations, directories, signature };
    channelClient.notify(documentationContract, 'restore', request);
  },

  setParent(parentId: string, documentations: number[], directories: number[]): void {
    channelClient.notify(documentationContract, 'setParent', { parentId, documentations, directories });
  },

  sync(): void {
    channelClient.notify(documentationContract, 'sync', undefined);
  },

  /** Rejects when the server returns no signed URL. */
  getSignedUrl(request: GetDocumentationSignedRequestModel): Promise<DocumentationSignedUrls> {
    return channelClient.request(documentationContract, 'getSignedUrl', request, RequestPolicies.read);
  },

  /** Every documentation event; `switch (event.type)` narrows `event.data`. */
  onEvent(
    handler: (event: ContractEvent<ContractShapeOf<typeof documentationContract>>) => void
  ): ChannelUnsubscribe {
    return channelClient.onEvent(documentationContract, handler);
  },

  onUpdated(
    handler: (event: ContractEventOf<typeof documentationContract, DomainMessagesTypes.updatedEntity>) => void
  ): ChannelUnsubscribe {
    return channelClient.onEventOfType(documentationContract, DomainMessagesTypes.updatedEntity, handler);
  },
};
